import {
    FC, useState, useCallback,
} from 'react';
import {
    AutoComplete, Button, Input, Row, Col, Space, Spin, Tooltip,
} from 'antd';
import { FiRefreshCw } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

import { getGeocodingUrl } from '../../../utils/mapbox.util';
import { CoverageProvider } from './context/coverage.provider';
import { useCoverageContext } from './context/coverage.ctx';
import { CoverageMap } from './CoverageMap';
import { CoverageList } from './CoverageList';

interface SearchResult {
    value: string;
    label: string;
    lng: number;
    lat: number;
}

const CoverageContent: FC = () => {
    const {
        loading, isDrawing, setIsDrawing, setSelected, fetchCoverages,
    } = useCoverageContext();
    const { t } = useTranslation('settings', { keyPrefix: 'SETTINGS.COVERAGE' });

    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [pin, setPin] = useState<{ lng: number; lat: number }>({ lng: -78.4678, lat: -0.1807 });
    const [flyTo, setFlyTo] = useState<{ lng: number; lat: number } | null>(null);

    const handleSearch = useCallback((value: string) => {
        if (!value || value.length < 3) {
            setSearchResults([]);
            return;
        }

        fetch(getGeocodingUrl(value))
            .then((res) => res.json())
            .then((data) => {
                const results: SearchResult[] = (data.features || []).map((f: any) => ({
                    value: f.place_name,
                    label: f.place_name,
                    lng: f.center[0],
                    lat: f.center[1],
                }));
                setSearchResults(results);
            })
            .catch(() => setSearchResults([]));
    }, []);

    const handleSelectLocation = useCallback((_: string, option: any) => {
        const { lng, lat } = option as SearchResult;
        setPin({ lng, lat });
        setFlyTo({ lng, lat });
    }, []);

    return (
        <Spin spinning={loading} style={{ width: '100%' }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col>
                        <AutoComplete
                            style={{ width: 320 }}
                            options={searchResults}
                            onSearch={handleSearch}
                            onSelect={handleSelectLocation}
                        >
                            <Input.Search
                                placeholder={t('SEARCH_LOCATION')}
                                allowClear
                            />
                        </AutoComplete>
                    </Col>
                    <Col>
                        <Space>
                            {isDrawing && (
                                <Button
                                    danger
                                    onClick={() => setIsDrawing(false)}
                                >
                                    {t('CANCEL_DRAWING')}
                                </Button>
                            )}
                            <Tooltip title={t('REFRESH')}>
                                <Button
                                    icon={<FiRefreshCw />}
                                    onClick={fetchCoverages}
                                    loading={loading}
                                />
                            </Tooltip>
                            <Button
                                type="primary"
                                onClick={() => {
                                    setSelected(null);
                                    setIsDrawing(true);
                                }}
                                disabled={isDrawing}
                            >
                                {isDrawing ? t('DRAWING') : t('ADD')}
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <div style={{
                    display: 'flex',
                    gap: 16,
                    height: 'calc(100vh - 380px)',
                    minHeight: 400,
                    maxHeight: 550,
                }}
                >
                    <div style={{ flex: 7, borderRadius: 8, overflow: 'hidden' }}>
                        <CoverageMap pin={pin} flyTo={flyTo} onFlyToComplete={() => setFlyTo(null)} />
                    </div>
                    <div style={{ flex: 3, minWidth: 280 }}>
                        <CoverageList />
                    </div>
                </div>
            </Space>
        </Spin>
    );
};

const Coverage: FC = () => (
    <CoverageProvider>
        <CoverageContent />
    </CoverageProvider>
);

export { Coverage };
